import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { forceVisible } from 'react-lazyload';
import { useTranslation } from 'react-i18next';
// Types
import { RootState } from 'app/store';
// Store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Actions
import PageActions from 'app/store/page/page.actions';
// Selectors
import { selectTrialMode } from 'app/store/AppUI/AppUI.selectors';
import { selectEpisodeLabelsAsOptions, selectDocumentEpisodeLabelOptions } from 'app/store/Episodes/Episodes.selectors';
import { selectFilter } from 'app/store/page/page.selectors';
// Mui
import { Box, Checkbox } from '@mui/material';
// Components
import { Autocomplete } from 'app/components/Mui/Autocompletes';
import { PagesToolbarActions } from 'app/store/pagesToolbar/pagesToolbar.slice';

const EpisodeLabelsSelect = () => {
  const { t } = useTranslation();

  const { documentId } = useParams<{ documentId:string; }>();

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const trialMode = useAppSelector(selectTrialMode);
  const filter = useAppSelector(selectFilter);
  const documentEpisodeLabelOptions = useAppSelector((state:RootState) => selectDocumentEpisodeLabelOptions(state, {
    documentId: +documentId
  })) as (string[] | null);
  // ToDO
  const episodeLabelOptions = useAppSelector(selectEpisodeLabelsAsOptions) as any;
  // End: ToDO

  const options = useMemo(() => {
    // if no documentId, it will mean that we are on the workspace page
    if ( !documentEpisodeLabelOptions ) return episodeLabelOptions || [];
    return documentEpisodeLabelOptions;
  }, [documentEpisodeLabelOptions, episodeLabelOptions]);

  const handleChange = (newValue:unknown) => {
    dispatch(PageActions.setFilter('labels', newValue as string[]));
    dispatch(PagesToolbarActions.clearSelectedPages());
    forceVisible();
  }

  if ( trialMode ) return null;
  return (
    <Autocomplete
      sx={{
        '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
          minWidth: 'unset !important',
        }
      }}
      value={filter.labels}
      onChange={handleChange}
      options={options}
      TextFieldProps={{
        label: t('labels.labels'),
        size: 'small',
        margin: 'none'
      }}
      ListboxProps={{
        sx: {
          // Reduce option horizontal padding
          '& .MuiAutocomplete-option': {
            px: 2
          }
        }
      }}
      multiple
      limitTags={2}
      getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.value}>
          <Checkbox sx={{ p: 1, pr: 3 }} checked={filter.labels.includes(option.value as string)} size="small" />
          {option.label}
        </Box>
      )}
      ChipProps={{
        sx: { maxWidth: `92px !important` },
        size: 'small'
      }}
      disableCloseOnSelect
    />
  )
}

export default EpisodeLabelsSelect;
